import { BRAND } from "#app/lib/shared/brand";
import { formatName } from "#app/lib/shared/utils";

type MetaDescriptor =
  | { title: string }
  | { name: string; content: string }
  | { property: string; content: string }
  | { tagName: "link"; rel: string; href: string };

export function noIndex(): MetaDescriptor[] {
  return [{ name: "robots", content: "noindex, nofollow" }];
}

// `Sign in — Marryly Match`.
export function pageTitle(page: string): string {
  return `${page} — ${BRAND}`;
}

function noIndexFollow(): MetaDescriptor[] {
  return [{ name: "robots", content: "noindex, follow" }];
}

function canonical(url: string): MetaDescriptor[] {
  return [{ tagName: "link", rel: "canonical", href: url }];
}

type OpenGraphInput = {
  url: string;
  title: string;
  description: string;
};

function openGraph({ url, title, description }: OpenGraphInput): MetaDescriptor[] {
  const image = new URL("/images/og.png", url).toString();

  return [
    { property: "og:type", content: "website" },
    { property: "og:site_name", content: BRAND },
    { property: "og:url", content: url },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:image", content: image },
    { property: "og:image:type", content: "image/png" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:alt", content: `${BRAND} logo` },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:image", content: image },
    { name: "twitter:image:alt", content: `${BRAND} logo` },
  ];
}

export function publicPageMeta(
  title: string,
  description: string,
  data: { canonicalUrl: string } | undefined,
): MetaDescriptor[] {
  const base: MetaDescriptor[] = [{ title }, { name: "description", content: description }];

  if (!data) return base;

  return [
    ...base,
    ...canonical(data.canonicalUrl),
    ...openGraph({ url: data.canonicalUrl, title, description }),
  ];
}

type ProfileMetaData = {
  canonicalUrl: string;
  profile: {
    firstName: string;
    age: number;
    country: string;
  };
};

export function profileMeta(
  segmentLabel: string,
  data: ProfileMetaData | undefined,
): MetaDescriptor[] {
  const robots = noIndexFollow();

  if (!data) return robots;

  const firstName = formatName(data.profile.firstName);
  const { profile } = data;
  const title = pageTitle(`${firstName}, ${profile.age} — ${segmentLabel} profiles`);
  // The segment label is a place name, so it reads as a possessive, not a modifier.
  const description = `${firstName}, ${profile.age}, from ${profile.country}. A profile from ${BRAND}'s ${segmentLabel} roster, introduced by our matchmakers.`;

  return [
    ...robots,
    { title },
    { name: "description", content: description },
    ...canonical(data.canonicalUrl),
    ...openGraph({ url: data.canonicalUrl, title, description }),
  ];
}
